import React, { useState, useEffect, useCallback } from 'react';
import { Select, Input, InputNumber, message } from 'antd';
import {
    apiGetSalaryBasics,
    apiAddSalaryBasic,
    apiUpdateSalaryBasic,
    apiDeleteSalaryBasic,
    apiGetBasicCode,
    apiGetSalaryBasicFuncs
} from '../../api';

// --- Helper Functions ---
const formatInputNumber = (value) => {
    if (value === null || value === undefined || isNaN(value) || Number(value) === 0) return '';
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const inputParser = (value) => value.replace(/,/g, '');

const initialFormData = {
    Bwhsno: '',
    FUNC: '',
    CODE: '',
    COUNTRY: '',
    BONUS: 0,
    DESCP: '',
    UNIT: '',
    JOB: '',
};

const EmpSalaryBasicManagement = () => {
    // --- State Management ---
    const [salaryBasics, setSalaryBasics] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    
    const [filters, setFilters] = useState({ bwhsno: 'ALL', country: 'ALL', func: 'ALL' });
    
    const [whsnoOptions, setWhsnoOptions] = useState([]);
    const [countryOptions, setCountryOptions] = useState([]);
    const [funcOptions, setFuncOptions] = useState([]);

    const [isFormVisible, setIsFormVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [formData, setFormData] = useState(initialFormData);

    // --- Data Fetching ---
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const data = await apiGetSalaryBasics(filters);
            setSalaryBasics(data);
        } catch (error) {
            message.error(`Không thể tải dữ liệu: ${error.message}`);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const fetchFilterOptions = useCallback(async () => {
        try {
            const [whsnoData, countryData, funcData] = await Promise.all([
                apiGetBasicCode('WHSNO'),
                apiGetBasicCode('COUNTRY'),
                apiGetSalaryBasicFuncs()
            ]);
            setWhsnoOptions(whsnoData.map(item => ({ label: `${item.SYS_VALUE} (${item.SYS_TYPE})`, value: item.SYS_TYPE })));
            setCountryOptions(countryData.map(item => ({ label: `${item.SYS_VALUE} (${item.SYS_TYPE})`, value: item.SYS_TYPE })));
            setFuncOptions(funcData.map(item => ({ label: item.FUNC, value: item.FUNC })));
        } catch (error) {
            message.error('Không thể tải dữ liệu cho các bộ lọc.');
        }
    }, []);

    useEffect(() => {
        fetchFilterOptions();
    }, [fetchFilterOptions]);
    
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // --- Handlers ---
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };
    
    const handleFormChange = (key, value) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    const handleAdd = () => {
        setEditingRecord(null);
        setFormData(initialFormData);
        setIsFormVisible(true);
    };

    const handleEdit = (record) => {
        setEditingRecord(record);
        setFormData(record);
        setIsFormVisible(true);
    };
    
    const handleCancel = () => {
        setIsFormVisible(false);
        setEditingRecord(null);
    };

    const handleDelete = async (autoid) => {
        if (!window.confirm("Bạn có chắc chắn muốn xóa mục này?")) return;
        try {
            await apiDeleteSalaryBasic(autoid);
            message.success('Xóa thành công');
            fetchData();
        } catch (error) {
            message.error(`Lỗi khi xóa: ${error.message}`);
        }
    };

    const handleSave = async () => {
        // Basic validation
        if (!formData.Bwhsno || !formData.FUNC || !formData.CODE || !formData.COUNTRY) {
            message.error('Vui lòng điền vào các trường bắt buộc: Xưởng, Loại, Mã Lương, Quốc Gia.');
            return;
        }
        setIsSaving(true);
        try {
            if (editingRecord) {
                await apiUpdateSalaryBasic(editingRecord.AUTOID, formData);
                message.success('Cập nhật thành công');
            } else {
                await apiAddSalaryBasic(formData);
                message.success('Thêm mới thành công');
            }
            setIsFormVisible(false);
            setEditingRecord(null);
            fetchData();
        } catch (error) {
            message.error(`Lưu thất bại: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };
    
    const tableHeaders = [
        { key: 'Bwhsno', label: 'Xưởng' },
        { key: 'FUNC', label: 'Loại' },
        { key: 'CODE', label: 'Mã Lương' },
        { key: 'COUNTRY', label: 'Quốc Gia' },
        { key: 'BONUS', label: 'Tiền Thưởng', isNumeric: true },
        { key: 'DESCP', label: 'Mô Tả' },
        { key: 'UNIT', label: 'Đơn Vị' },
        { key: 'JOB', label: 'Chức Vụ' },
        { key: 'action', label: 'Hành động', width: '150px' },
    ];

    return (
        <div className="p-4" style={{ fontFamily: '"Times New Roman", Times, serif' }}>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">QUẢN LÝ LƯƠNG CƠ BẢN</h2>

            <div className="bg-gray-50 p-4 rounded-lg flex flex-wrap items-end gap-4 mb-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Xưởng</label>
                    <Select
                        className="mt-1"
                        style={{ width: 200 }}
                        value={filters.bwhsno}
                        onChange={(value) => handleFilterChange('bwhsno', value)}
                        options={[{ label: 'Tất cả', value: 'ALL' }, ...whsnoOptions]}
                    />
                </div>
                 <div>
                    <label className="block text-sm font-medium text-gray-700">Quốc Gia</label>
                    <Select
                        className="mt-1"
                        style={{ width: 200 }}
                        value={filters.country}
                        onChange={(value) => handleFilterChange('country', value)}
                        options={[{ label: 'Tất cả', value: 'ALL' }, ...countryOptions]}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Loại (FUNC)</label>
                    <Select
                        className="mt-1"
                        style={{ width: 200 }}
                        value={filters.func}
                        onChange={(value) => handleFilterChange('func', value)}
                        options={[{ label: 'Tất cả', value: 'ALL' }, ...funcOptions]}
                    />
                </div>
                <button onClick={fetchData} disabled={loading} className="px-6 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400">
                    {loading ? 'Đang tải...' : 'Lọc'}
                </button>
                <button onClick={handleAdd} className="px-6 py-2 font-semibold text-white bg-green-600 rounded-md hover:bg-green-700">
                    Thêm Mới
                </button>
            </div>

            {isFormVisible && (
                <div className="bg-gray-50 p-4 rounded-lg mb-6 border border-blue-200">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">{editingRecord ? 'Chỉnh sửa Lương Cơ Bản' : 'Thêm Lương Cơ Bản Mới'}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Xưởng *</label>
                            <Select value={formData.Bwhsno} onChange={(v) => handleFormChange('Bwhsno', v)} options={whsnoOptions} style={{ width: '100%' }} className="mt-1" />
                        </div>
                        <div>
                           <label className="block text-sm font-medium text-gray-700">Loại (FUNC) *</label>
                           <Select value={formData.FUNC} onChange={(v) => handleFormChange('FUNC', v)} options={funcOptions} style={{ width: '100%' }} className="mt-1" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Mã Lương *</label>
                            <Input value={formData.CODE} onChange={(e) => handleFormChange('CODE', e.target.value)} className="mt-1" />
                        </div>
                        <div>
                           <label className="block text-sm font-medium text-gray-700">Quốc Gia *</label>
                           <Select value={formData.COUNTRY} onChange={(v) => handleFormChange('COUNTRY', v)} options={countryOptions} style={{ width: '100%' }} className="mt-1" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Tiền Thưởng</label>
                            <InputNumber value={formData.BONUS} onChange={(v) => handleFormChange('BONUS', v)} formatter={formatInputNumber} parser={inputParser} style={{ width: '100%' }} className="mt-1" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Mô Tả</label>
                            <Input value={formData.DESCP} onChange={(e) => handleFormChange('DESCP', e.target.value)} className="mt-1" />
                        </div>
                         <div>
                            <label className="block text-sm font-medium text-gray-700">Đơn Vị</label>
                            <Input value={formData.UNIT} onChange={(e) => handleFormChange('UNIT', e.target.value)} className="mt-1" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Chức Vụ</label>
                            <Input value={formData.JOB} onChange={(e) => handleFormChange('JOB', e.target.value)} className="mt-1" />
                        </div>
                    </div>
                    <div className="flex justify-end gap-4 mt-4">
                        <button onClick={handleCancel} className="px-6 py-2 font-semibold text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">
                            Hủy
                        </button>
                        <button onClick={handleSave} disabled={isSaving} className="px-6 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400">
                            {isSaving ? 'Đang lưu...' : 'Lưu'}
                        </button>
                    </div>
                </div>
            )}
            
            <div className="overflow-auto bg-white rounded-lg shadow border" style={{ maxHeight: '70vh' }}>
                 <table className="min-w-full text-sm">
                    <thead className="sticky top-0 bg-gray-100">
                        <tr>
                            {tableHeaders.map(header => (
                                <th key={header.key} className={`p-2 font-semibold text-gray-600 whitespace-nowrap border-b border-r ${header.isNumeric ? 'text-right' : 'text-left'}`} style={{width: header.width}}>
                                    {header.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {loading ? (
                            <tr><td colSpan={tableHeaders.length} className="p-4 text-center">Đang tải dữ liệu...</td></tr>
                        ) : salaryBasics.length === 0 ? (
                             <tr><td colSpan={tableHeaders.length} className="p-4 text-center text-gray-500">Không có dữ liệu</td></tr>
                        ) : salaryBasics.map(row => (
                            <tr key={row.AUTOID} className="hover:bg-gray-50">
                                {tableHeaders.map(header => (
                                    <td key={`${row.AUTOID}-${header.key}`} className={`p-2 whitespace-nowrap border-r ${header.isNumeric ? 'text-right' : 'text-left'}`}>
                                        {header.key === 'action' ? (
                                            <div className="flex space-x-2">
                                                <button onClick={() => handleEdit(row)} className="text-blue-600 hover:underline">Sửa</button>
                                                <button onClick={() => handleDelete(row.AUTOID)} className="text-red-600 hover:underline">Xóa</button>
                                            </div>
                                        ) : header.key === 'BONUS' ? (
                                            formatInputNumber(row[header.key])
                                        ) : (
                                            row[header.key]
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default EmpSalaryBasicManagement;
